import { MOBILE_URL } from 'constants/constants';
import { useStorageData } from 'hooks/useStorageData';
import extractHostnameFromUrl from 'utilities/extractHostnameFromUrl/extractHostnameFromUrl';

type ParamValue = string | number | boolean | undefined | null;

/** The slice of the stored homedata blob this hook reads. */
interface FirmIdentityStorage {
  company_url?: string;
  cpa_uuid?: string;
}

export interface GenerateUrlOptions {
  /**
   * If true, appends the firm-identity params: company_name derived from the
   * current CPA URL, plus cpa_uuid when available (the exact firm key — the
   * new frontend prefers it over the company_name host match, LISD-2544).
   * Defaults to true
   */
  includeCompanyName?: boolean;

  /**
   * Additional query parameters to include in the URL
   */
  extraParams?: Record<string, ParamValue>;
}

/**
 * Generic URL generator for external/mobile domain redirects.
 * Mirrors behavior of useGenerateNewRequestsListPageUrl but works for any path.
 *
 * - Uses MOBILE_URL as the host (or development override via redirectedFrom)
 * - Optionally appends company_name from stored CPA url
 * - Accepts arbitrary extra query params
 */
export const useGenerateExternalUrl = (
  pathName: string,
  options?: GenerateUrlOptions,
): string => {
  const { storageData } = useStorageData();
  const { company_url: currentlyLoggedCpaUrl, cpa_uuid: cpaUuid } =
    (storageData as FirmIdentityStorage | null) ?? {};
  const companyName = extractHostnameFromUrl(currentlyLoggedCpaUrl ?? '');

  const params: string[] = [];

  // Include the firm-identity params by default. Both name the same firm:
  // cpa_uuid is exact and preferred by the receiver; company_name stays for
  // receivers that only understand the host form.
  if (options?.includeCompanyName !== false) {
    if (companyName) {
      params.push(`company_name=${encodeURIComponent(companyName)}`);
    }
    if (cpaUuid) {
      params.push(`cpa_uuid=${encodeURIComponent(cpaUuid)}`);
    }
  }

  // Add any extra params (skip undefined/null)
  if (options?.extraParams) {
    Object.entries(options.extraParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
        );
      }
    });
  }

  // In non-production, attach redirectedFrom and allow session override of host
  if (import.meta.env.VITE_ENVIRONMENT !== 'production') {
    params.push(
      `redirectedFrom=${encodeURIComponent(
        `${window.location.protocol}//${window.location.host}`,
      )}`,
    );
  }

  const host =
    import.meta.env.VITE_ENVIRONMENT !== 'production' &&
    sessionStorage.getItem('redirectedFrom')
      ? sessionStorage.getItem('redirectedFrom')
      : MOBILE_URL;

  const query = params.length ? `?${params.join('&')}` : '';

  return `${host || ''}${pathName}${query}`;
};

export default useGenerateExternalUrl;
